import {
  AlarmCallEvent,
  AlarmCallType,
  AlarmCallEventAudioPhase,
  AlarmCallEventChatPhase,
  AlarmCallEventContentType,
  AlarmCallParticipant,
  AlarmLogCallSessionPhase,
  AlarmLogEvent,
  AlarmLogEventKind,
} from '@x-guard/xgac-types/xgac';
import { uniqBy } from 'lodash';

export type EventRow = {
  kind: 'event';
  id: string;
  ids: string[];
  at: string;
  event: AlarmLogEvent;
  /** Set on a notification row once delivery was confirmed (folded in from the confirmed event). */
  confirmedAt?: string;
  /** Set on a notification row when the person opened the alarm (folded in from the viewed event). */
  viewedAt?: string;
};

/** Started / join / leave / ended of one person in one call, as a single item. */
export type CallRow = {
  kind: 'call';
  id: string;
  ids: string[];
  /** Time of the latest session event, used for ordering. */
  at: string;
  startedAt?: string;
  /** Alarm call or helper call. */
  callType?: AlarmCallType;
  intervals: { from: string; to?: string }[];
  /** What this person sent in that call: messages, positions, recordings. */
  counts: CallCounts;
};

export type CallCounts = {
  chat: number;
  audio: number;
  position: number;
  recording: number;
  /** Total length of the recordings. */
  recordingMs: number;
};

const emptyCounts = (): CallCounts => ({
  chat: 0, audio: 0, position: 0, recording: 0, recordingMs: 0,
});

/** One thing a person sent in a call: a message, a position update or a recording (from the call events or the operator chat). */
export type MessageEntry = {
  id: string;
  at: string;
  person: string;
  kind: 'chat' | 'audio' | 'position' | 'recording';
  call?: string;
  /** Audio file of a published voice message. */
  src?: string;
  /** Sent by an operator: only operator chat says for sure who is an operator (helpers act as users too). */
  operator?: boolean;
  /** The chat text, or the transcript of a voice message when available. */
  text?: string;
  /** Recordings only: total length and number of chunks. */
  durationMs?: number;
  chunks?: number;
};

/** A single message, as shown in the chronological log. */
export type MessageRow = {
  kind: 'message';
  id: string;
  ids: string[];
  at: string;
  entry: MessageEntry;
};

/** Message counts of a person who sent messages without being in a call. */
export type MessagesRow = {
  kind: 'messages';
  id: string;
  ids: string[];
  at: string;
  counts: CallCounts;
};

/** Everything done to reach one person (notification, SMS, e-mail, calls), oldest first. */
export type SummonRow = {
  kind: 'summon';
  id: string;
  ids: string[];
  at: string;
  lines: EventRow[];
  /** The person's latest answer is that they come to help. */
  helping: boolean;
};

export type LogRow = EventRow | CallRow | SummonRow | MessageRow | MessagesRow;

export type PersonRole = 'creator' | 'helper' | 'operator';

export type LogGroups = {
  system: LogRow[];
  people: { name: string; role?: PersonRole; rows: LogRow[] }[];
};

/** Who is who on this alarm, to label the person blocks. */
export type RoleContext = {
  creatorName?: string;
  helperNames?: ReadonlySet<string>;
};

export const eventRow = (event: AlarmLogEvent): EventRow => ({
  kind: 'event',
  id: event._id,
  ids: [event._id],
  at: event.createdAt,
  event,
});

export const messageRow = (entry: MessageEntry): MessageRow => ({
  kind: 'message',
  id: entry.id,
  ids: [entry.id],
  at: entry.at,
  entry,
});

/**
 * What call participants sent: published chat and voice messages, position updates and first-moment recordings
 * (one entry per recording, not per chunk), plus the operator's own chat messages.
 */
export const messageEntries = (
  callEvents: AlarmCallEvent[],
  participants: AlarmCallParticipant[],
  operatorMessages: AlarmLogEvent[],
  operatorName: (event: AlarmLogEvent) => string,
  unknownName: string,
  creator?: { _id: string; name: string },
  operatorNames?: ReadonlyMap<string, string>,
): MessageEntry[] => {

  // The alarm creator's app makes the first-moment recording itself; the call participant behind it can be anonymous.
  const personOfEvent = (event: AlarmCallEvent) => {

    const { type } = event.content;
    const byCreator = creator && (
      type === AlarmCallEventContentType.FirstMomentRecording
      || event.asset?._id === creator._id
    );

    if (byCreator) return creator.name;

    // The same participant can sit in several stores; a copy with a redacted (blank) name must not hide one with a name.
    const copies = participants.filter(({ _id }) => _id === event.participant);
    const participant = copies.find(({ name }) => name) ?? copies[0];

    // A participant whose name is redacted is an anonymous operator: label it like the operator's other rows.
    return participant?.name || (participant?.ref ? operatorNames?.get(participant.ref._id) : undefined) || unknownName;

  };
  const recordings = new Map<string, MessageEntry>();

  // A chunk stored twice (an upload retry) counts once, preferring the published copy.
  const isPublished = (event: AlarmCallEvent) => (
    event.content.type === AlarmCallEventContentType.FirstMomentRecording
    && event.content.state.phase === AlarmCallEventAudioPhase.Published
  );
  const uniqueEvents = uniqBy(
    [...callEvents].sort((a, b) => Number(isPublished(b)) - Number(isPublished(a))),
    (event) => (event.content.type === AlarmCallEventContentType.FirstMomentRecording
      ? `${event.content.recordingId}|${event.content.chunkIndex}`
      : event._id),
  );

  const fromCalls = uniqueEvents.flatMap((event): MessageEntry[] => {

    const { content } = event;
    const person = personOfEvent(event);
    const call = event.alarmCall._id;

    if (content.type === AlarmCallEventContentType.Chat && content.state.phase === AlarmCallEventChatPhase.Published) {

      return [{
        id: event._id, at: content.state.publishedAt, person, kind: 'chat', call, text: content.state.text,
      }];

    }

    if (
      content.type === AlarmCallEventContentType.Audio
      && (content.state.phase === AlarmCallEventAudioPhase.Published || content.state.phase === AlarmCallEventAudioPhase.Processing)
    ) {

      return [{
        id: event._id,
        at: content.state.phase === AlarmCallEventAudioPhase.Published ? content.state.publishedAt : event.createdAt,
        person,
        kind: 'audio',
        call,
        text: content.state.metadata?.transcript?.text ?? undefined,
        src: content.state.src ?? undefined,
      }];

    }

    if (content.type === AlarmCallEventContentType.PositionUpdate) {

      return [{
        id: event._id, at: event.createdAt, person, kind: 'position', call,
      }];

    }

    if (content.type === AlarmCallEventContentType.FirstMomentRecording) {

      const known = recordings.get(content.recordingId);
      const chunkMs = content.state.phase === AlarmCallEventAudioPhase.Published ? content.state.metadata.durationMs : 0;

      if (known) {

        known.chunks = (known.chunks ?? 0) + 1;
        known.durationMs = (known.durationMs ?? 0) + chunkMs;
        if (Date.parse(event.createdAt) < Date.parse(known.at)) known.at = event.createdAt;

        return [];

      }

      const entry: MessageEntry = {
        id: `recording-${content.recordingId}`,
        at: event.createdAt,
        person,
        kind: 'recording',
        call,
        durationMs: chunkMs,
        chunks: 1,
      };

      recordings.set(content.recordingId, entry);

      return [entry];

    }

    return [];

  });

  const fromOperator = operatorMessages
    .filter((event) => event.data?.eventType === AlarmLogEventKind.OperatorChat)
    .map((event): MessageEntry => ({
      id: event._id,
      at: event.createdAt,
      person: operatorName(event),
      kind: 'chat',
      operator: true,
      text: event.data?.eventType === AlarmLogEventKind.OperatorChat ? event.data.message : undefined,
    }));

  return [...fromCalls, ...fromOperator];

};

// Messages of one person closer together than this belong to the same counter row.
const messageSlotMs = 10 * 60 * 1000;

const outreachKinds = new Set<AlarmLogEventKind>([
  AlarmLogEventKind.Notification,
  AlarmLogEventKind.Sms,
  AlarmLogEventKind.Email,
  AlarmLogEventKind.ActionCall,
  AlarmLogEventKind.ActionCallStaticResponder,
  AlarmLogEventKind.ActionCallToStaticResponder,
  AlarmLogEventKind.ActionCallToDynamicResponder,
]);

export const byNewest = (a: LogRow, b: LogRow) => Date.parse(b.at) - Date.parse(a.at);

/** The person an event is about: the recipient for outgoing actions, otherwise whoever created it. */
export const personOf = (event: AlarmLogEvent, operatorNames: Map<string, string>): string | undefined => {

  const { data, createdBy } = event;

  switch (data?.eventType) {

  case AlarmLogEventKind.Notification:
  case AlarmLogEventKind.NotificationConfirmedDelivery:
    return data.to.name;
  case AlarmLogEventKind.AlarmViewedInApp:
    return data.by.name;
  case AlarmLogEventKind.Sms:
  case AlarmLogEventKind.Email:
  case AlarmLogEventKind.ActionCall:
  case AlarmLogEventKind.ActionCallStaticResponder:
  case AlarmLogEventKind.ActionCallToStaticResponder:
  case AlarmLogEventKind.ActionCallToDynamicResponder:
    return data.name;
  default:
    break;

  }

  if (!createdBy || ('isServiceAccount' in createdBy && createdBy.isServiceAccount)) return undefined;

  return createdBy.name?.trim() || operatorNames.get(createdBy._id);

};

/** One call row per person per call; leave closes the open join. */
const addCallSession = (row: CallRow, event: AlarmLogEvent, phase: AlarmLogCallSessionPhase) => {

  const open = row.intervals.find((interval) => !interval.to);

  row.ids.push(event._id);
  row.at = event.createdAt;

  if (phase === AlarmLogCallSessionPhase.Started) row.startedAt = event.createdAt;
  // A join while already connected (a reconnect without leave) adds nothing, so one leave always closes the call.
  if (phase === AlarmLogCallSessionPhase.Join && !open) row.intervals.push({ from: event.createdAt });

  if (phase === AlarmLogCallSessionPhase.Leave || phase === AlarmLogCallSessionPhase.Ended) {

    if (open) open.to = event.createdAt;
    // Known limit: a leave without a join is shown from the call start; it never guesses older sessions.
    else if (phase === AlarmLogCallSessionPhase.Leave) row.intervals.push({ from: row.startedAt ?? event.createdAt, to: event.createdAt });

  }

};

/**
 * System block first, then one block per person (most recent activity first), each newest first.
 * - Confirmed/viewed events are folded into the person's latest earlier notification row.
 * - A person's call started/join/leave events become one call row per call.
 * - Every outreach action to one person (notification, SMS, e-mail, call) becomes one summon row.
 * Anything without a match stays its own row, so nothing is lost.
 * Known limit: a person is identified by display name (the logs carry no shared id across notification, call and chat
 * events), so two people with the same name share one card; the upgrade path is a stable id on those events.
 */
export const groupLogs = (
  logs: AlarmLogEvent[],
  operatorNames: Map<string, string>,
  messages: MessageEntry[] = [],
  roles: RoleContext = {},
): LogGroups => {

  const rows: { row: LogRow; person?: string }[] = [];
  const latestNotification = new Map<string, EventRow>();
  const calls = new Map<string, CallRow>();
  const operators = new Set<string>();

  [...logs]
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
    .forEach((event) => {

      const person = personOf(event, operatorNames);
      const { data } = event;
      const notification = person ? latestNotification.get(person) : undefined;

      if (notification && data?.eventType === AlarmLogEventKind.NotificationConfirmedDelivery) {

        notification.confirmedAt ??= event.createdAt;
        notification.ids.push(event._id);
        return;

      }

      if (notification && data?.eventType === AlarmLogEventKind.AlarmViewedInApp) {

        notification.viewedAt = event.createdAt;
        notification.ids.push(event._id);
        return;

      }

      // The call is over for everyone, whoever (or whatever) wrote the event.
      if (data?.eventType === AlarmLogEventKind.CallSession && data.phase === AlarmLogCallSessionPhase.Ended) {

        calls.forEach((call, key) => {

          if (key.endsWith(`|${data.alarmCall?._id ?? ''}`)) {

            call.intervals.forEach((interval) => {

              interval.to ??= event.createdAt;

            });

          }

        });

      }

      const callKey = `${person}|${data?.eventType === AlarmLogEventKind.CallSession ? data.alarmCall?._id ?? '' : ''}`;

      // An end by someone who never took part has no call row to close: it stays a plain event instead of an empty call.
      if (person && data?.eventType === AlarmLogEventKind.CallSession && (data.phase !== AlarmLogCallSessionPhase.Ended || calls.has(callKey))) {

        const existing = calls.get(callKey);
        const call = existing ?? {
          kind: 'call' as const,
          id: `call-${event._id}`,
          ids: [],
          at: event.createdAt,
          callType: data.callType,
          intervals: [],
          counts: emptyCounts(),
        };

        addCallSession(call, event, data.phase);

        if (!existing) {

          calls.set(callKey, call);
          rows.push({ row: call, person });

        }

        return;

      }

      const row = eventRow(event);

      if (data?.eventType === AlarmLogEventKind.Notification) {

        row.confirmedAt = data.confirmedDeliveryAt ?? undefined;
        if (person) latestNotification.set(person, row);

      }

      rows.push({ row, person });

    });

  const standalone = new Map<string, MessagesRow>();

  // Messages count towards the person's call; without a call id (operator chat) they get a counter row per time slot.
  [...messages]
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .forEach((entry) => {

      // A voice message gets its own row (transcript and play button) instead of being counted.
      if (entry.kind === 'audio') {

        rows.push({ row: messageRow(entry), person: entry.person });
        return;

      }

      const call = entry.call ? calls.get(`${entry.person}|${entry.call}`) : undefined;
      const open = standalone.get(entry.person);
      // Messages without a call are counted per time slot: a pause longer than the slot starts a new counter row.
      const current = open && Date.parse(entry.at) - Date.parse(open.at) <= messageSlotMs ? open : undefined;
      const target = call ?? current ?? (() => {

        const created: MessagesRow = {
          kind: 'messages', id: `messages-${entry.id}`, ids: [], at: entry.at, counts: emptyCounts(),
        };

        standalone.set(entry.person, created);
        rows.push({ row: created, person: entry.person });

        return created;

      })();

      if (entry.operator) operators.add(entry.person);
      target.counts[entry.kind] += 1;
      if (entry.kind === 'recording') target.counts.recordingMs += entry.durationMs ?? 0;
      target.ids.push(entry.id);
      if (Date.parse(entry.at) > Date.parse(target.at)) target.at = entry.at;

    });

  const system: LogRow[] = [];
  const people = new Map<string, LogRow[]>();

  rows.forEach(({ row, person }) => {

    if (!person) {

      system.push(row);
      return;

    }

    people.set(person, [...(people.get(person) ?? []), row]);

  });

  const withSummon = (personRows: LogRow[]): LogRow[] => {

    const outreach = personRows.filter((row): row is EventRow => (
      row.kind === 'event' && !!row.event.data && outreachKinds.has(row.event.data.eventType)
    ));

    if (outreach.length === 0) return personRows;

    const answers = personRows
      .filter((row): row is EventRow => row.kind === 'event' && row.event.data?.eventType === AlarmLogEventKind.ResponseConfirmation)
      .sort(byNewest);
    const latest = answers[0]?.event.data;
    const helping = latest?.eventType === AlarmLogEventKind.ResponseConfirmation && latest.value;
    // The "helping" label says it, so the separate yes-answers would be repeats; a no stays visible.
    const folded = helping
      ? answers.filter(({ event }) => event.data?.eventType === AlarmLogEventKind.ResponseConfirmation && event.data.value)
      : [];

    const summon: SummonRow = {
      kind: 'summon',
      id: `summon-${outreach[0].id}`,
      ids: [...outreach, ...folded].flatMap((row) => row.ids),
      at: outreach.reduce((latestAt, row) => (Date.parse(row.at) > Date.parse(latestAt) ? row.at : latestAt), outreach[0].at),
      lines: outreach,
      helping,
    };

    return [...personRows.filter((row) => !outreach.includes(row as EventRow) && !folded.includes(row as EventRow)), summon];

  };

  return {
    system: system.sort(byNewest),
    people: [...people.entries()]
      .map(([name, personRows]) => {

        const role = ((): PersonRole | undefined => {

          if (name === roles.creatorName) return 'creator';
          if (operators.has(name)) return 'operator';

          return roles.helperNames?.has(name) ? 'helper' : undefined;

        })();

        return { name, role, rows: withSummon(personRows).sort(byNewest) };

      })
      .sort((a, b) => byNewest(a.rows[0], b.rows[0])),
  };

};
